import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Event } from '../types';

interface FavoritesContextType {
  favorites: Event[];
  addToFavorites: (event: Event) => void;
  removeFromFavorites: (eventId: string) => void;
  isFavorite: (eventId: string) => boolean;
  clearFavorites: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const [favorites, setFavorites] = useState<Event[]>([]);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('findfunnow-favorites');
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (error) {
        console.error('Error loading favorites from localStorage:', error);
      }
    }
  }, []);

  // Save favorites to localStorage whenever favorites change
  useEffect(() => {
    localStorage.setItem('findfunnow-favorites', JSON.stringify(favorites));
  }, [favorites]);

  const addToFavorites = (event: Event) => {
    setFavorites(prev => {
      const isAlreadyFavorite = prev.some(fav => fav.uniqueId === event.uniqueId);
      if (isAlreadyFavorite) {
        return prev;
      }
      return [...prev, event];
    });
  };

  const removeFromFavorites = (eventId: string) => {
    setFavorites(prev => prev.filter(fav => fav.uniqueId !== eventId));
  };

  const isFavorite = (eventId: string) => {
    return favorites.some(fav => fav.uniqueId === eventId);
  };

  const clearFavorites = () => {
    setFavorites([]);
  };

  return (
    <FavoritesContext.Provider value={{
      favorites,
      addToFavorites,
      removeFromFavorites,
      isFavorite,
      clearFavorites
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};